import { useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { DbMilestone } from "@/types/database";
import Icon from "@/components/feature/Icon";

export default function TimelineSection() {
  const [milestones, setMilestones] = useState<DbMilestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [visibleItems, setVisibleItems] = useState<Set<number>>(new Set());
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  useEffect(() => {
    async function fetchMilestones() {
      const { data, error } = await supabase
        .from("milestones")
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true })
        .order("id", { ascending: true });
      if (!error && data) {
        setMilestones(data as DbMilestone[]);
      }
      setLoading(false);
    }
    fetchMilestones();
  }, []);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    milestones.forEach((m) => {
      const el = itemRefs.current.get(m.id);
      if (!el) return;
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setVisibleItems((prev) => new Set(prev).add(m.id));
            }
          });
        },
        { threshold: 0.3 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [milestones]);

  if (loading) {
    return (
      <section className="py-24 md:py-32 bg-navy-950">
        <div className="px-6 md:px-10 lg:px-16 text-center">
          <Icon className="ri-loader-4-line animate-spin text-2xl text-silver-400 inline-block mb-2" />
          <p className="text-sm text-silver-400">載入中...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 md:py-32 bg-navy-950">
      <div className="px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div className="max-w-3xl mb-16 md:mb-20">
          <p className="text-sm font-medium tracking-[0.2em] text-silver-500 uppercase mb-4">
            Our Journey
          </p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-white leading-tight">
            發展歷程
          </h2>
        </div>

        {/* Timeline */}
        <div className="relative max-w-4xl mx-auto">
          {/* Vertical line */}
          <div className="absolute left-4 md:left-1/2 md:-translate-x-px top-0 bottom-0 w-px bg-white/10" />

          <div className="space-y-12 md:space-y-16">
            {milestones.map((milestone, index) => {
              const isLeft = index % 2 === 0;
              const isVisible = visibleItems.has(milestone.id);

              return (
                <div
                  key={milestone.id}
                  ref={(el) => {
                    if (el) itemRefs.current.set(milestone.id, el);
                  }}
                  className={`relative flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-0 transition-all duration-700 ${
                    isVisible
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-8"
                  }`}
                >
                  {/* Dot on the line */}
                  <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-white -translate-x-1/2 md:-translate-x-1.5 mt-2 md:mt-0 ring-4 ring-navy-950" />

                  {/* Date badge - mobile always left */}
                  <div
                    className={`md:w-1/2 ${
                      isLeft ? "md:text-right md:pr-12" : "md:order-2 md:pl-12"
                    } pl-10 md:pl-0`}
                  >
                    <span className="inline-block bg-white/10 backdrop-blur-sm text-white text-sm font-medium px-4 py-1.5 rounded-full">
                      {milestone.date}
                    </span>
                  </div>

                  {/* Content */}
                  <div
                    className={`md:w-1/2 ${
                      isLeft ? "md:order-2 md:pl-12" : "md:text-right md:pr-12"
                    } pl-10 md:pl-0`}
                  >
                    <h3 className="text-lg md:text-xl font-semibold text-white mb-2">
                      {milestone.title}
                    </h3>
                    <p className="text-silver-400 text-sm md:text-base leading-relaxed">
                      {milestone.description || ""}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}